import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { NgbActiveOffcanvas } from '@ng-bootstrap/ng-bootstrap';

export type LegalDoc = 'terms' | 'privacy';

interface ILegalText { title: string; updated: string; sections: { title: string; text: string }[]; }

/** Textos legales (010/011). Texto plano: títulos + párrafos (regla 13). */
export const LEGAL_TEXTS: Record<LegalDoc, ILegalText> = {
  terms: {
    title: 'Términos y condiciones',
    updated: 'Última actualización: 1 de octubre de 2026',
    sections: [
      { title: 'Aceptación', text: 'Al crear una cuenta en la consola de CradleRewards aceptas estos Términos y condiciones. Si no estás de acuerdo, no debes registrarte ni usar la consola.' },
      { title: 'Uso de la consola', text: 'La consola te permite administrar tus negocios, diseñar tarjetas de lealtad y consultar la actividad de tus clientes. Eres responsable del contenido que publicas.' },
      { title: 'Tarjetas y recompensas', text: 'Cada negocio define cuántos sellos se necesitan, qué recompensa entrega y sus condiciones. Te comprometes a entregar las recompensas que publiques.' },
      { title: 'Tu cuenta', text: 'Eres responsable de la confidencialidad de tu contraseña y de toda la actividad que ocurra en tu cuenta.' },
      { title: 'Cambios a los términos', text: 'Podemos actualizar estos términos. Te avisaremos por correo antes de que entren en vigor.' },
      { title: 'Contacto', text: 'Si tienes dudas, escríbenos a soporte@cradlerewards.mx.' },
    ],
  },
  privacy: {
    title: 'Aviso de privacidad',
    updated: 'Última actualización: 1 de octubre de 2026',
    sections: [
      { title: 'Responsable', text: 'CradleRewards es responsable del tratamiento de tus datos personales.' },
      { title: 'Datos que recabamos', text: 'Tu nombre, apellidos y correo electrónico, así como los datos de tus negocios: nombre, logos, ubicación, horario y redes sociales.' },
      { title: 'Para qué los usamos', text: 'Para darte acceso a la consola, mostrar tus negocios en la app y enviarte avisos sobre tu cuenta.' },
      { title: 'Datos de tus clientes', text: 'Solo ves el nombre, el correo y la actividad de los clientes que tienen tu tarjeta. Úsalos únicamente para tu programa de lealtad.' },
      { title: 'Tus derechos', text: 'Puedes acceder, rectificar, cancelar u oponerte al uso de tus datos escribiendo a privacidad@cradlerewards.mx.' },
    ],
  },
};

/** Panel lateral derecho de 560 px (celular: hoja a pantalla completa). «Cerrar», clic en el velo o Esc. */
@Component({
  selector: 'app-legal-panel',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <header class="head">
      <h2 id="legal-title">{{ text().title }}</h2>
      <button type="button" class="close" (click)="ref.dismiss()">Cerrar</button>
    </header>
    <div class="body">
      <p class="updated">{{ text().updated }}</p>
      @for (s of text().sections; track s.title) {
        <section><h3>{{ s.title }}</h3><p>{{ s.text }}</p></section>
      }
    </div>
  `,
  styles: [`
    :host{display:flex;flex-direction:column;height:100%;background:#fff}
    .head{height:74px;flex:none;display:flex;align-items:center;justify-content:space-between;padding:0 32px;border-bottom:1px solid var(--cr-border-subtle)}
    h2{margin:0;font:600 20px/24px var(--cr-font-display)}
    .close{border:0;background:none;color:var(--cr-brand-teal);font:600 15px/22px var(--cr-font-body);cursor:pointer;padding:8px}
    .body{flex:1;overflow-y:auto;padding:24px 32px 32px;display:flex;flex-direction:column;gap:12px}
    .updated{margin:0;font:400 13px/18px var(--cr-font-body);color:var(--cr-text-tertiary)}
    section{padding-top:12px;display:flex;flex-direction:column;gap:8px}
    h3{margin:0;font:600 16px/22px var(--cr-font-body)}
    section p{margin:0;font:400 14px/22px var(--cr-font-body);color:var(--cr-text-secondary)}
    @media (max-width:640px){ .head{padding:0 16px 0 20px} .body{padding:20px} }
  `],
})
export class LegalPanelComponent {
  readonly ref = inject(NgbActiveOffcanvas);
  readonly doc = signal<LegalDoc>('terms');
  readonly text = computed(() => LEGAL_TEXTS[this.doc()]);
}
